// Consumes the SSE-formatted responses from the platform console's two
// long-running tenant operations: POST .../provision and POST .../delete.
//
// Native EventSource can't be used here: it only supports a GET request with
// no custom headers, but both calls need a POST body and an
// `Authorization: Bearer <token>` header (never a query-string token — that
// would leak the operator's session into server logs / browser history).
// fetch() + a manual ReadableStream reader does both. The frame-parsing loop
// is shared (streamPlatformOperation) since provisioning and deletion only
// differ in URL, request body shape, and what their 'step'/'complete' event
// payloads look like — duplicating the parser per operation would be pure
// copy-paste.
import { platformAuthHeader } from './client';

export interface StepEvent {
  step: string;
  status: 'start' | 'progress' | 'done';
  message: string;
}

export interface ErrorEvent {
  message: string;
}

interface StreamHandlers<TComplete> {
  onStep: (event: StepEvent) => void;
  onComplete: (event: TComplete) => void;
  onError: (event: ErrorEvent) => void;
}

/** Parses one `event: <name>\ndata: <json>\n\n` frame at a time out of the
 * raw SSE text as it streams in — a minimal parser rather than a dependency,
 * since this app talks to exactly two SSE endpoints, both shaped alike. */
async function streamPlatformOperation<TComplete>(
  url: string,
  body: unknown,
  handlers: StreamHandlers<TComplete>
): Promise<void> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...platformAuthHeader() },
    body: JSON.stringify(body),
  });

  if (!res.body) throw new Error('Streaming is not supported in this browser');
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error || `Request failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let eventName = 'message';
      let dataLine = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
      }
      if (!dataLine) continue;
      const data = JSON.parse(dataLine);

      if (eventName === 'step') handlers.onStep(data as StepEvent);
      else if (eventName === 'complete') handlers.onComplete(data as TComplete);
      else if (eventName === 'error') handlers.onError(data as ErrorEvent);
    }
  }
}

// ── Provisioning ─────────────────────────────────────────────────

export interface ProvisionCompleteEvent {
  slug: string;
  adminUser: string | null;
  adminPassword: string | null;
  adminPasswordGenerated: boolean;
  tables: number;
  admins: number;
}

interface ProvisionBody {
  schema?: string;
  plan?: string;
  databaseUrl?: string;
  adminUser?: string;
  adminPassword?: string;
  adminName?: string;
  skipSeed?: boolean;
}

export function streamProvisioning(
  tenantId: number | string,
  body: ProvisionBody,
  handlers: StreamHandlers<ProvisionCompleteEvent>
): Promise<void> {
  return streamPlatformOperation(`/platform-api/tenants/${tenantId}/provision`, body, handlers);
}

// ── Deletion ─────────────────────────────────────────────────────

export interface DeleteCompleteEvent {
  slug: string;
  dumpKey: string;
  tables: number;
}

export function streamDeletion(
  tenantId: number | string,
  confirmSlug: string,
  handlers: StreamHandlers<DeleteCompleteEvent>
): Promise<void> {
  return streamPlatformOperation(`/platform-api/tenants/${tenantId}/delete`, { confirmSlug }, handlers);
}

// ── Restore ──────────────────────────────────────────────────────

export interface RestoreCompleteEvent {
  slug: string;
  restoredFrom: string;
  safetyBackupKey: string;
  migrationsApplied: number;
}

export function streamRestore(
  tenantId: number | string,
  body: { backupKey: string; confirmSlug: string },
  handlers: StreamHandlers<RestoreCompleteEvent>
): Promise<void> {
  return streamPlatformOperation(`/platform-api/tenants/${tenantId}/restore`, body, handlers);
}
